import { AppBar, Toolbar, Typography, Link, Box, Stack, Divider } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "Components/Context";
import { resolveDonationConfig } from "services/donations";

const Footer = () => {
  const { t, i18n } = useTranslation("common");
  const { user } = React.useContext(UserContext);
  const donationConfig = React.useMemo(
    () => resolveDonationConfig({ user }),
    [user]
  );

  const primaryLinks = [
    { href: `/${i18n.language}/before-you-win`, label: t("home.beforeYouWin") },
    { href: `/${i18n.language}/how-it-works`, label: t("footer.howItWorks") },
    { href: `/${i18n.language}/pricing`, label: t("footer.pricing") },
    { href: `/${i18n.language}/mission`, label: t("footer.mission") },
  ];

  const legalLinks = [
    { href: `/${i18n.language}/terms-of-use`, label: t("footer.terms") },
    { href: `/${i18n.language}/privacy-policy`, label: t("footer.privacy") },
  ];

  return (
    <AppBar
      position="relative"
      color="default"
      elevation={0}
      sx={{
        top: 'auto', bottom: 0,
        backgroundColor: "transparent",
        borderTop: "2px dotted",
        borderLeft: "none",
        borderBottom: "none",
        borderImage: "linear-gradient(90deg, rgba(60, 74, 83, 0.90), rgba(60, 74, 83, .00)) 1"
      }}>
      <Toolbar
        variant="dense"
        sx={{
          backgroundColor: "transparent",
          top: 'auto',
          bottom: 0,
          py: 0.75,
          minHeight: { xs: 96, sm: 84 },
        }}
      >
        <Stack
          direction="column"
          spacing={0.5}
          // gap={.5}
          display={"flex"}
          flexGrow={1}
          alignItems="center"
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0, sm: 3 },
            }}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{ my: 0.5, display: { xs: "none", sm: "block" } }}
              />
            }
          >
            <Box sx={{
              display: "flex",
              gap: 3,
              fontSize: "smaller",
            }}>
              {primaryLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  underline="hover"
                  sx={{ color: "inherit", cursor: "pointer", fontSize: "inherit" }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
            <Box sx={{
              display: "flex",
              gap: 3,
              fontSize: "smaller",
            }}>
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  underline="hover"
                  sx={{ color: "inherit", cursor: "pointer", fontSize: "inherit" }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Stack>
          <Box>
            <Typography fontSize="x-small" color="inherit" textAlign="center">
              {t("footer.allRights", { year: new Date().getFullYear() })}
              {" · "}
              <Link
                href={donationConfig.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                title={t("footer.supportHint", { currency: donationConfig.currency })}
                aria-label={t("footer.supportAria")}
                underline="hover"
                tabIndex={donationConfig.isVisible ? 0 : -1}
                aria-hidden={!donationConfig.isVisible}
                onClick={(event) => {
                  if (!donationConfig.isVisible) {
                    event.preventDefault();
                  }
                }}
                sx={{
                  color: "text.secondary",
                  cursor: donationConfig.isVisible ? "pointer" : "default",
                  textUnderlineOffset: "0.12em",
                  visibility: donationConfig.isVisible ? "visible" : "hidden",
                  "&:hover": { color: "text.primary" },
                  "&:focus-visible": {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: "2px",
                    borderRadius: "2px",
                  },
                }}
              >
                {t("footer.supportProject")}
              </Link>
            </Typography>
            <Typography fontSize="x-small" textAlign="center" sx={{ opacity: 0.8 }}>
              {t("footer.noPrediction")}
            </Typography>
          </Box>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default Footer;
